import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { CreateGHADiscountRuleBody, GHADiscountRuleAccountType } from '~/services/ConnectionGoogleService';

import {
  GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS,
  GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX,
  GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX,
  GHA_DISCOUNT_TYPE_OPTIONS,
  MAX_PERCENTAGE_DISCOUNT,
} from './constants';
import { GHADiscountRuleFormState, toCreateGHADiscountRuleBody } from './utils';

interface Props {
  open: boolean;
  initialPropertyId?: string;
  lockPropertyId?: boolean;
  accountTypeOptions?: Array<{ value: GHADiscountRuleAccountType; label: string }>;
  defaultAccountType?: GHADiscountRuleAccountType;
  isSaving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onCreate: (input: CreateGHADiscountRuleBody) => Promise<void>;
}

type FormField = keyof GHADiscountRuleFormState;

function getInitialState(defaults: {
  propertyId?: string;
  accountType?: GHADiscountRuleAccountType;
}): GHADiscountRuleFormState {
  return {
    propertyId: defaults.propertyId ?? '',
    accountType: defaults.accountType,
    discountType: 'percentage',
    discountValue: '',
    minAdvanceBookingWindow: '0',
    minLengthOfStay: '1',
  };
}

function getDiscountValueLabel(discountType: GHADiscountRuleFormState['discountType']) {
  return discountType === 'margin_retention' ? 'Retained Margin Percentage' : 'Discount Percentage';
}

function getDiscountValueHelperText(discountType: GHADiscountRuleFormState['discountType']) {
  return discountType === 'margin_retention'
    ? `Margin to retain from the incoming rate (0 - ${MAX_PERCENTAGE_DISCOUNT}).`
    : `Percentage off (0 - ${MAX_PERCENTAGE_DISCOUNT}).`;
}

function buildErrors(form: GHADiscountRuleFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  const discountValue = Number(form.discountValue);
  const minAdvanceBookingWindow = Number(form.minAdvanceBookingWindow);
  const minLengthOfStay = Number(form.minLengthOfStay);

  if (!form.propertyId.trim()) {
    errors.propertyId = 'Property ID is required';
  }
  if (!form.accountType) {
    errors.accountType = 'Account type is required';
  }
  if (form.discountValue === '' || !Number.isFinite(discountValue) || discountValue < 0) {
    errors.discountValue = `${getDiscountValueLabel(form.discountType)} must be 0 or greater`;
  } else if (discountValue > MAX_PERCENTAGE_DISCOUNT) {
    errors.discountValue = `${getDiscountValueLabel(form.discountType)} cannot exceed ${MAX_PERCENTAGE_DISCOUNT}%`;
  }
  if (
    !Number.isInteger(minAdvanceBookingWindow) ||
    minAdvanceBookingWindow < 0 ||
    minAdvanceBookingWindow > GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX
  ) {
    errors.minAdvanceBookingWindow = `Advance booking window must be 0-${GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX}`;
  }
  if (
    !Number.isInteger(minLengthOfStay) ||
    minLengthOfStay < 1 ||
    minLengthOfStay > GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX
  ) {
    errors.minLengthOfStay = `Length of stay must be 1-${GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX}`;
  }

  return errors;
}

function GHADiscountRuleDialog({
  open,
  initialPropertyId,
  lockPropertyId,
  accountTypeOptions = GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS,
  defaultAccountType,
  isSaving,
  submitError,
  onClose,
  onCreate,
}: Props) {
  const initialAccountType = defaultAccountType ?? accountTypeOptions[0]?.value;
  const [form, setForm] = useState<GHADiscountRuleFormState>(() =>
    getInitialState({ propertyId: initialPropertyId, accountType: initialAccountType }),
  );
  const [touchedFields, setTouchedFields] = useState<Partial<Record<FormField, boolean>>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(getInitialState({ propertyId: initialPropertyId, accountType: initialAccountType }));
      setTouchedFields({});
      setHasSubmitted(false);
    }
  }, [initialAccountType, initialPropertyId, open]);

  const errors = useMemo(() => buildErrors(form), [form]);
  const displayedErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(errors).filter(([field]) => hasSubmitted || touchedFields[field as FormField]),
      ) as Record<string, string>,
    [errors, hasSubmitted, touchedFields],
  );
  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    setHasSubmitted(true);
    if (!isValid || !form.accountType) {
      return;
    }

    await onCreate(toCreateGHADiscountRuleBody({ ...form, accountType: form.accountType }));
  };

  const updateForm = <K extends keyof GHADiscountRuleFormState>(key: K, value: GHADiscountRuleFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const markFieldTouched = (field: FormField) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create GHA Discount Rule</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <TextField
              label="Property ID"
              value={form.propertyId}
              onChange={(event) => updateForm('propertyId', event.target.value)}
              onBlur={() => markFieldTouched('propertyId')}
              required
              disabled={lockPropertyId}
              fullWidth
              error={!!displayedErrors.propertyId}
              helperText={displayedErrors.propertyId || 'UUID of the Google-connected property.'}
            />
            <FormControl
              fullWidth
              required
              error={!!displayedErrors.accountType}
              disabled={accountTypeOptions.length === 0}
            >
              <InputLabel>Account Type</InputLabel>
              <Select
                label="Account Type"
                value={form.accountType ?? ''}
                onChange={(event) => updateForm('accountType', event.target.value ? event.target.value : undefined)}
                onBlur={() => markFieldTouched('accountType')}
              >
                {accountTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {displayedErrors.accountType && <FormHelperText>{displayedErrors.accountType}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Discount Type</InputLabel>
              <Select
                label="Discount Type"
                value={form.discountType}
                onChange={(event) =>
                  updateForm('discountType', event.target.value as GHADiscountRuleFormState['discountType'])
                }
              >
                {GHA_DISCOUNT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={getDiscountValueLabel(form.discountType)}
              type="number"
              value={form.discountValue}
              onChange={(event) => updateForm('discountValue', event.target.value)}
              onBlur={() => markFieldTouched('discountValue')}
              required
              fullWidth
              error={!!displayedErrors.discountValue}
              helperText={displayedErrors.discountValue || getDiscountValueHelperText(form.discountType)}
              inputProps={{ min: 0, max: MAX_PERCENTAGE_DISCOUNT, step: 0.01 }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Min Advance Booking Window"
                type="number"
                value={form.minAdvanceBookingWindow}
                onChange={(event) => updateForm('minAdvanceBookingWindow', event.target.value)}
                onBlur={() => markFieldTouched('minAdvanceBookingWindow')}
                required
                fullWidth
                error={!!displayedErrors.minAdvanceBookingWindow}
                helperText={displayedErrors.minAdvanceBookingWindow || 'Minimum days before check-in.'}
                inputProps={{ min: 0, max: GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX, step: 1 }}
              />
              <TextField
                label="Min Length of Stay"
                type="number"
                value={form.minLengthOfStay}
                onChange={(event) => updateForm('minLengthOfStay', event.target.value)}
                onBlur={() => markFieldTouched('minLengthOfStay')}
                required
                fullWidth
                error={!!displayedErrors.minLengthOfStay}
                helperText={displayedErrors.minLengthOfStay || 'Minimum nights.'}
                inputProps={{ min: 1, max: GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX, step: 1 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isSaving}>
            Create
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default GHADiscountRuleDialog;
